/* eslint-disable react-refresh/only-export-components */
//Disable because Fast refresh only works when a file only exports components
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState
} from 'react';
import { defaultMarketFilterParams, defaultMarketRefParams } from './default';
import { SearchParams } from '../enums';
import {
  AdsFilterDataType,
  AdsRefDataType,
  AdsWeightDataType,
  MarketFilterDataType,
  MarketRefDataType,
  SoldFilterDataType,
  SoldRefDataType,
  SoldWeightDataType,
} from '../types/dataTypes';
import {
  defaultAdsFilterParams,
  defaultAdsRefParams,
  defaultAdsWeightParams,
  resetAdsFilterParams,
} from '@/context/AdsDefault';
import {
  defaultSoldFilterParams,
  defaultSoldRefParams,
  defaultSoldWeightParams,
  resetSoldFilterParams,
} from '@/context/SoldDefault';

export interface SoldDataState {
  refData: SoldRefDataType;
  filterData: SoldFilterDataType;
  weightData: SoldWeightDataType;
  isFilterActive: boolean;
}

export interface AdsDataState {
  refData: AdsRefDataType;
  filterData: AdsFilterDataType;
  weightData: AdsWeightDataType;
  isFilterActive: boolean;
}

export interface MarketDataState {
  refData: MarketRefDataType;
  filterData: MarketFilterDataType;
  isFilterActive: boolean;
}

interface FilterContextType {
  adsData: AdsDataState;
  soldData: SoldDataState;
  marketData: MarketDataState;
  isFilterHidden: boolean;
  resetAdsParams: (state: SearchParams) => void;
  resetSoldParams: (state: SearchParams) => void;
  resetMarketParams: (state: SearchParams) => void;
  resetSoldAndAdParams: (state: SearchParams) => void;
  setAdsParam: (state: SearchParams, label: string, value: any) => any;
  setAdsParams: (
    refData: Partial<AdsRefDataType>,
    weightData: Partial<AdsWeightDataType>,
    filterData: Partial<AdsFilterDataType>
  ) => any;
  setMarketParam: (state: SearchParams, label: string, value: any) => any;
  setMarketParams: (filterData: Partial<MarketFilterDataType>) => any;
  setSoldParam: (state: SearchParams, label: string, value: any) => any;
  setSoldParams: (
    refData: Partial<SoldRefDataType>,
    weightData: Partial<SoldWeightDataType>,
    filterData: Partial<SoldFilterDataType>
  ) => any;
  setIsFilterHidden: Dispatch<SetStateAction<boolean>>;
  setSoldAndAdParam: (state: SearchParams, label: string, value: any) => void;
}

const defaultFilterContext: FilterContextType = {
  adsData: {
    refData: defaultAdsRefParams,
    filterData: defaultAdsFilterParams,
    weightData: defaultAdsWeightParams,
    isFilterActive: false,
  },
  soldData: {
    refData: defaultSoldRefParams,
    filterData: defaultSoldFilterParams,
    weightData: defaultSoldWeightParams,
    isFilterActive: false,
  },
  marketData: {
    refData: defaultMarketRefParams,
    filterData: defaultMarketFilterParams,
    isFilterActive: false,
  },
  isFilterHidden: false,
  resetAdsParams: () => { },
  resetMarketParams: () => { },
  resetSoldParams: () => { },
  setAdsParam: () => { },
  setAdsParams: () => { },
  setMarketParam: () => { },
  setMarketParams: () => { },
  setSoldParam: () => { },
  setSoldParams: () => { },
  setIsFilterHidden: () => { },
  setSoldAndAdParam: () => { },
  resetSoldAndAdParams: () => { },
};

const FilterContext = createContext<FilterContextType>(defaultFilterContext);

interface FilterProviderProps {
  children?: ReactNode;
}

const FilterProvider: FC<FilterProviderProps> = ({ children }) => {
  const [isFilterHidden, setIsFilterHidden] = useState<boolean>(false);
  const [adsData, setAdsData] = useState<AdsDataState>({
    refData: defaultAdsRefParams,
    filterData: defaultAdsFilterParams,
    weightData: defaultAdsWeightParams,
    isFilterActive: false,
  });
  const [soldData, setSoldData] = useState<SoldDataState>({
    refData: defaultSoldRefParams,
    filterData: defaultSoldFilterParams,
    weightData: defaultSoldWeightParams,
    isFilterActive: false,
  });
  const [marketData, setMarketData] = useState<MarketDataState>({
    refData: defaultMarketRefParams,
    filterData: defaultMarketFilterParams,
    isFilterActive: false,
  });

  const setSoldParams = (
    refData: Partial<SoldRefDataType>,
    weightData: Partial<SoldWeightDataType>,
    filterData: Partial<SoldFilterDataType>
  ) => {
    const orgNr = !!filterData.owner_id;
    setSoldData({
      refData: { ...defaultSoldRefParams, ...refData },
      weightData: { ...defaultSoldWeightParams, ...weightData },
      filterData: { ...defaultSoldFilterParams, orgNr, ...filterData },
      isFilterActive: false,
    });
  };

  const setAdsParams = (
    refData: Partial<AdsRefDataType>,
    weightData: Partial<AdsWeightDataType>,
    filterData: Partial<AdsFilterDataType>
  ) => {
    setAdsData({
      refData: { ...defaultAdsRefParams, ...refData },
      weightData: { ...defaultAdsWeightParams, ...weightData },
      filterData: { ...defaultAdsFilterParams, ...filterData },
      isFilterActive: false,
    });
  };

  const setMarketParams = (filterData: Partial<MarketFilterDataType>) => {
    setMarketData((prevState) => ({
      ...prevState,
      filterData: { ...defaultMarketFilterParams, ...filterData },
    }));
  };

  const setMarketParam = (state: SearchParams, label: string, value: any) => {
    switch (state) {
      case SearchParams.ref:
        setMarketData((prevState) => ({
          ...prevState,
          refData: { ...prevState.refData, [label]: value },
        }));
        break;
      case SearchParams.filter:
        setMarketData((prevState) => ({
          ...prevState,
          filterData: { ...prevState.filterData, [label]: value },
          isFilterActive: true,
        }));
        break;
      default:
        break;
    }
  };

  const setSoldParam = (state: SearchParams, label: string, value: any) => {
    switch (state) {
      case SearchParams.ref:
        setSoldData((prevState) => ({
          ...prevState,
          refData: { ...prevState.refData, [label]: value },
        }));
        break;
      case SearchParams.weight:
        setSoldData((prevState) => ({
          ...prevState,
          weightData: { ...prevState.weightData, [label]: value },
        }));
        break;
      case SearchParams.filter:
        setSoldData((prevState) => ({
          ...prevState,
          filterData: { ...prevState.filterData, [label]: value },
          isFilterActive: true,
        }));
        break;
      default:
        break;
    }
  };

  const setAdsParam = (state: SearchParams, label: string, value: any) => {
    switch (state) {
      case SearchParams.ref:
        setAdsData((prevState) => ({
          ...prevState,
          refData: { ...prevState.refData, [label]: value },
        }));
        break;
      case SearchParams.weight:
        setAdsData((prevState) => ({
          ...prevState,
          weightData: { ...prevState.weightData, [label]: value },
        }));
        break;
      case SearchParams.filter:
        setAdsData((prevState) => ({
          ...prevState,
          filterData: { ...prevState.filterData, [label]: value },
          isFilterActive: true,
        }));
        break;
      default:
        break;
    }
  };

  const resetMarketParams = (state: SearchParams) => {
    switch (state) {
      case SearchParams.all:
        setMarketData((prevState) => ({
          ...prevState,
          refData: defaultMarketRefParams,
          filterData: defaultMarketFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.ref:
        setMarketData((prevState) => ({
          ...prevState,
          refData: defaultMarketRefParams,
        }));
        break;
      case SearchParams.filter:
        setMarketData((prevState) => ({
          ...prevState,
          filterData: defaultMarketFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.weightRef:
        setMarketData((prevState) => ({
          ...prevState,
          refData: defaultMarketRefParams,
        }));
        break;
    }
  };

  const resetSoldParams = (state: SearchParams) => {
    switch (state) {
      case SearchParams.all:
        setSoldData((prevState) => ({
          ...prevState,
          refData: defaultSoldRefParams,
          weightData: defaultSoldWeightParams,
          filterData: resetSoldFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.ref:
        setSoldData((prevState) => ({
          ...prevState,
          refData: defaultSoldRefParams,
        }));
        break;
      case SearchParams.weight:
        setSoldData((prevState) => ({
          ...prevState,
          weightData: defaultSoldWeightParams,
        }));
        break;
      case SearchParams.filter:
        setSoldData((prevState) => ({
          ...prevState,
          filterData: defaultSoldFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.weightRef:
        setSoldData((prevState) => ({
          ...prevState,
          weightData: defaultSoldWeightParams,
          refData: defaultSoldRefParams,
        }));
        break;
      case SearchParams.advancedFilter:
        setSoldData((prevState) => ({
          ...prevState,
          weightData: defaultSoldWeightParams,
          filterData: {
            ...soldData.filterData,
            home_type: null,
            ownership_type: null
          }
        }));
        break;
    }
  };

  const resetAdsParams = (state: SearchParams) => {
    switch (state) {
      case SearchParams.all:
        setAdsData((prevState) => ({
          ...prevState,
          refData: defaultAdsRefParams,
          weightData: defaultAdsWeightParams,
          filterData: resetAdsFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.ref:
        setAdsData((prevState) => ({
          ...prevState,
          refData: defaultAdsRefParams,
        }));
        break;
      case SearchParams.weight:
        setAdsData((prevState) => ({
          ...prevState,
          weightData: defaultAdsWeightParams,
        }));
        break;
      case SearchParams.filter:
        setAdsData((prevState) => ({
          ...prevState,
          filterData: defaultAdsFilterParams,
          isFilterActive: false,
        }));
        break;
      case SearchParams.weightRef:
        setAdsData((prevState) => ({
          ...prevState,
          weightData: defaultAdsWeightParams,
          refData: defaultAdsRefParams,
        }));
        break;
    }
  };

  const setSoldAndAdParam = (
    state: SearchParams,
    label: string,
    value: any
  ) => {
    setSoldParam(state, label, value);
    setAdsParam(state, label, value);
  };

  const resetSoldAndAdParams = (state: SearchParams) => {
    resetSoldParams(state);
    resetAdsParams(state);
  };

  return (
    <FilterContext.Provider
      value={{
        adsData,
        marketData,
        soldData,
        isFilterHidden,
        resetSoldParams,
        resetAdsParams,
        resetMarketParams,
        resetSoldAndAdParams,
        setAdsParam,
        setAdsParams,
        setMarketParam,
        setMarketParams,
        setSoldParam,
        setSoldParams,
        setIsFilterHidden,
        setSoldAndAdParam,
      }}>
      {children}
    </FilterContext.Provider>
  );
};

const useFilterContext = () => useContext(FilterContext);

export { FilterProvider, useFilterContext };
